import React, { createContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/auth.service';
import { backgroundService } from '../services/backgroundService';
import { registerWebPushDevice } from '../services/webPushService';
import { SESSION_TIMEOUT_MS } from '../utils/constants';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState(null);

    useEffect(() => {
        checkAuth();
    }, []);

    // Cleanup effect that only runs on unmount
    useEffect(() => {
        return () => {
            // This will only run when the component unmounts
            if (backgroundService.isRunning) {
                backgroundService.stop();
            }
        };
    }, []);

    const checkAuth = async () => {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const userData = await authService.getCurrentUser();
                setUser(userData);

                registerWebPushDevice().catch((err) =>
                    console.warn('Web push registration failed:', err)
                );
                
                // Start background service for agent role if user is already authenticated
                if (userData.role?.toLowerCase() === 'agent') {
                    backgroundService.start(userData, (notificationData) => {
                        setNotification(notificationData);
                    });
                }
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            localStorage.removeItem('token');
        } finally {
            setLoading(false);
        }
    };

    const login = async (username, password) => {
        const response = await authService.login(username, password);
        console.log('Login response:', response.user);
        setUser(response.user);

        registerWebPushDevice().catch((err) =>
            console.warn('Web push registration failed:', err)
        );
        
        // Start background service for agent role
        if (response.user.role?.toLowerCase() === 'agent') {
            backgroundService.start(response.user, (notificationData) => {
                setNotification(notificationData);
            });
        }
        
        return response;
    };

    const logout = useCallback(async () => {
        backgroundService.stop();

        try {
            await authService.logout();
        } catch (e) {
            console.error('Logout error:', e);
            localStorage.removeItem('token');
        }
        setUser(null);
        setNotification(null);
    }, []);

    useEffect(() => {
        if (!user || typeof window === 'undefined' || typeof document === 'undefined') {
            return undefined;
        }

        let lastActivity = Date.now();

        const bump = () => {
            lastActivity = Date.now();
        };

        const maybeLogout = () => {
            if (Date.now() - lastActivity >= SESSION_TIMEOUT_MS) {
                void logout();
            }
        };

        bump();
        const intervalId = window.setInterval(maybeLogout, 1000);

        const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
        const onActivity = () => {
            const now = Date.now();
            if (now - lastActivity < 750) return;
            bump();
        };
        events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                maybeLogout();
            }
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            window.clearInterval(intervalId);
            events.forEach((e) => window.removeEventListener(e, onActivity));
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [user, logout]);

    const value = {
        user,
        login,
        logout,
        loading,
        isAuthenticated: !!user,
        notification,
        setNotification,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};